import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  Share as ShareIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { usePublicBoardWinners } from './usePublicBoardWinners';
import { WinnersHero } from './WinnersHero';
import { WinnersGrid } from './WinnersGrid';
import { JoinCTA } from './JoinCTA';
import { ShareModal } from '../../components/shared/ShareModal';
import { useShare } from '../../hooks/useShare';
import logoSvg from '../../assets/logo.svg';

export const WinnersPage: React.FC = () => {
  const { boardSlug } = useParams<{ boardSlug: string }>();
  const navigate = useNavigate();
  const { board, winnerGroups, isLoading, error } =
    usePublicBoardWinners(boardSlug);
  const { share, modalOpen, shareOptions, closeModal, trackShare } = useShare();
  const confettiFired = useRef(false);

  // Celebratory confetti burst once data loads
  useEffect(() => {
    if (!isLoading && board && !confettiFired.current) {
      confettiFired.current = true;
      // Small delay so the page paints first
      requestAnimationFrame(() => {
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.3 },
          colors: ['#FFD700', '#FFA500', '#B8860B', '#DAA520'],
        });
      });
    }
  }, [isLoading, board]);

  // Loading state
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          bgcolor: '#0f0f1a',
        }}
      >
        <CircularProgress sx={{ color: '#FFD700' }} />
      </Box>
    );
  }

  // Error states
  if (error === 'not_found' || !board) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Typography variant="h5" sx={{ mb: 2 }}>
          Board not found
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          This board doesn&apos;t exist or isn&apos;t available for public
          viewing.
        </Typography>
        <Button
          variant="contained"
          startIcon={<HomeIcon />}
          onClick={() => navigate('/')}
        >
          Go Home
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" sx={{ mb: 2 }}>
          Something went wrong
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </Box>
    );
  }

  const shareUrl = `${window.location.origin}/b/${board.slug || board.id}/winners`;
  const winnerCount = winnerGroups.length;

  return (
    <>
      <Helmet>
        <title>{board.name} — Winners | SquareUp</title>
        <meta property="og:title" content={`${board.name} — Winners`} />
        <meta
          property="og:description"
          content={`${board.awayTeam} vs ${board.homeTeam} — ${winnerCount} winner${winnerCount !== 1 ? 's' : ''} on SquareUp`}
        />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`${board.name} — Winners`}
        />
        <meta
          name="twitter:description"
          content={`See who won in the ${board.awayTeam} vs ${board.homeTeam} squares pool!`}
        />
      </Helmet>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #f8f6f0 0%, #f0ebe0 100%)',
        }}
      >
        {/* AppBar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: '#1a1a2e',
            borderBottom: '1px solid rgba(255, 215, 0, 0.15)',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            >
              <img src={logoSvg} alt="SquareUp" style={{ height: 28 }} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Share winners">
                <IconButton
                  color="inherit"
                  onClick={() => {
                    share({
                      title: `${board.name} — Winners`,
                      text: `Check out the winners from ${board.awayTeam} vs ${board.homeTeam} on SquareUp!`,
                      url: shareUrl,
                      boardId: board.id,
                      shareContext: 'winners',
                    });
                  }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate(`/b/${board.slug || board.id}`)}
                sx={{ textTransform: 'none', opacity: 0.85, '&:hover': { opacity: 1 } }}
              >
                View Board
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Hero */}
        <WinnersHero board={board} />

        {/* Winners heading with shimmer */}
        <Box sx={{ textAlign: 'center', pt: 4, pb: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              background:
                'linear-gradient(90deg, #B8860B 0%, #FFD700 25%, #FFF8DC 50%, #FFD700 75%, #B8860B 100%)',
              backgroundSize: '200% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'shimmer 2s ease-in-out forwards',
              '@keyframes shimmer': {
                '0%': { backgroundPosition: '200% 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
            }}
          >
            Winners
          </Typography>
        </Box>

        {/* Winners Grid */}
        <Box sx={{ flex: 1 }}>
          <WinnersGrid winnerGroups={winnerGroups} board={board} />
        </Box>

        {/* Join CTA */}
        <JoinCTA board={board} />

        {/* Share Modal */}
        {shareOptions && (
          <ShareModal
            open={modalOpen}
            onClose={closeModal}
            title={shareOptions.title}
            text={shareOptions.text}
            url={shareOptions.url}
            boardId={shareOptions.boardId}
            shareContext={shareOptions.shareContext}
            onTrackShare={trackShare}
          />
        )}
      </Box>
    </>
  );
};
